import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import Card from '../../../shared/components/Card';
import axios from 'axios';
import { getApiBaseUrl } from '../../../core/config/api';

const securityLogsApiBase = (): string => getApiBaseUrl(true).replace(/\/$/, '');

interface SecurityLog {
  id: number;
  type: string;
  user_id: number | null;
  username: string | null;
  ip_address: string;
  user_agent: string | null;
  path: string;
  method: string;
  details: string | null;
  severity: 'low' | 'medium' | 'high' | 'critical';
  created_at: string;
}

interface SecurityStats {
  total: number;
  byType: Array<{ type: string; count: number }>;
  bySeverity: Array<{ severity: string; count: number }>;
  topIPs: Array<{ ip_address: string; count: number }>;
}

const SecurityLogs: React.FC = () => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();

  const [logs, setLogs] = useState<SecurityLog[]>([]);
  const [stats, setStats] = useState<SecurityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showSeverityLegend, setShowSeverityLegend] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    severity: '',
    user_id: '',
    ip_address: '',
    start_date: '',
    end_date: ''
  });

  // Vérifier les permissions
  const hasAccess = currentUser?.roles_id && currentUser.roles_id <= 3; // CM, Admin, Modo

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    if (!hasAccess) {
      navigate('/panel');
      return;
    }

    loadLogs();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, page, filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      });

      const response = await axios.get(
        `${securityLogsApiBase()}/security-logs?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setLogs(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
      }
    } catch (err: any) {
      console.error('Erreur lors du chargement des logs:', err);
      setError(err.response?.data?.error || 'Erreur lors du chargement des logs');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${securityLogsApiBase()}/security-logs/stats?days=7`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (err: any) {
      console.error('Erreur lors du chargement des statistiques:', err);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // Reset à la page 1 lors d'un changement de filtre
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      severity: '',
      user_id: '',
      ip_address: '',
      start_date: '',
      end_date: ''
    });
    setPage(1);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg shadow-red-500/50';
      case 'high':
        return 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/50';
      case 'medium':
        return 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-lg shadow-yellow-500/50';
      case 'low':
        return 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg shadow-green-500/50';
      default:
        return 'bg-gradient-to-r from-gray-500 to-gray-600 text-white';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'high':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'medium':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'auth_failure': 'Échec d\'authentification',
      'auth_success': 'Authentification réussie',
      'rate_limit_exceeded': 'Limite de requêtes dépassée',
      'unauthorized_access': 'Accès non autorisé',
      'suspicious_activity': 'Activité suspecte',
      'invalid_token': 'Token invalide',
      'permission_denied': 'Permission refusée',
      'data_tampering': 'Manipulation de données',
      'sql_injection_attempt': 'Tentative d\'injection SQL',
      'xss_attempt': 'Tentative XSS'
    };
    return labels[type] || type;
  };

  const getTypeIcon = (type: string) => {
    if (type.includes('auth')) {
      return '🔐';
    } else if (type.includes('rate_limit')) {
      return '⏱️';
    } else if (type.includes('unauthorized') || type.includes('permission')) {
      return '🚫';
    } else if (type.includes('suspicious')) {
      return '⚠️';
    } else if (type.includes('injection') || type.includes('xss')) {
      return '💉';
    }
    return '📋';
  };

  if (!currentUser || !hasAccess) {
    return null;
  }

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  return (
    <div className="min-h-screen relative overflow-hidden">
      <BackgroundImage />
      <ParticlesBackground />
      <Navbar />
      
      <div className="relative z-10 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header avec bouton retour */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <div className="bg-red-500/20 p-3 rounded-xl backdrop-blur-sm border border-red-500/30">
                  <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-5xl font-bold text-white mb-2 text-gradient">
                    Logs de Sécurité
                  </h1>
                  <p className="text-gray-300 text-lg">
                    Surveillance et analyse des événements de sécurité en temps réel
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate('/panel')}
              className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl flex items-center space-x-2 backdrop-blur-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Retour au panel</span>
            </button>
          </div>

          {error && (
            <div className="mb-6 bg-red-500/20 border border-red-500/50 text-red-200 p-4 rounded-xl backdrop-blur-sm shadow-lg">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Statistiques avec design moderne */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="bg-gradient-to-br from-blue-600/30 to-blue-800/30 border-blue-500/50 backdrop-blur-sm hover:scale-105 transition-transform duration-300 shadow-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-300 text-sm font-medium mb-1">Total (7 jours)</p>
                    <p className="text-4xl font-bold text-white">{stats.total}</p>
                  </div>
                  <div className="bg-blue-500/20 p-4 rounded-xl">
                    <svg className="w-8 h-8 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
              </Card>

              <Card className="bg-gradient-to-br from-red-600/30 to-red-800/30 border-red-500/50 backdrop-blur-sm hover:scale-105 transition-transform duration-300 shadow-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-300 text-sm font-medium mb-1">Critiques</p>
                    <p className="text-4xl font-bold text-white">
                      {stats.bySeverity.find(s => s.severity === 'critical')?.count || 0}
                    </p>
                  </div>
                  <div className="bg-red-500/20 p-4 rounded-xl">
                    <svg className="w-8 h-8 text-red-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </Card>

              <Card className="bg-gradient-to-br from-orange-600/30 to-orange-800/30 border-orange-500/50 backdrop-blur-sm hover:scale-105 transition-transform duration-300 shadow-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-300 text-sm font-medium mb-1">Élevés</p>
                    <p className="text-4xl font-bold text-white">
                      {stats.bySeverity.find(s => s.severity === 'high')?.count || 0}
                    </p>
                  </div>
                  <div className="bg-orange-500/20 p-4 rounded-xl">
                    <svg className="w-8 h-8 text-orange-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </Card>

              <Card className="bg-gradient-to-br from-yellow-600/30 to-yellow-800/30 border-yellow-500/50 backdrop-blur-sm hover:scale-105 transition-transform duration-300 shadow-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-yellow-300 text-sm font-medium mb-1">Moyens</p>
                    <p className="text-4xl font-bold text-white">
                      {stats.bySeverity.find(s => s.severity === 'medium')?.count || 0}
                    </p>
                  </div>
                  <div className="bg-yellow-500/20 p-4 rounded-xl">
                    <svg className="w-8 h-8 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Filtres avec accordéon */}
          <Card className="mb-6 bg-gray-900/60 backdrop-blur-sm border-gray-700/50">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center space-x-2">
                <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span>Filtres de recherche</span>
                {hasActiveFilters && (
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full">
                    Actifs
                  </span>
                )}
              </h2>
              <div className="flex items-center space-x-2">
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-sm bg-gray-700/50 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
                  >
                    Réinitialiser
                  </button>
                )}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <svg className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  <span>{showFilters ? 'Masquer' : 'Afficher'}</span>
                </button>
              </div>
            </div>
            
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 pt-4 border-t border-gray-700/50">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Type d'événement</label>
                  <select
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                  >
                    <option value="">Tous les types</option>
                    <option value="auth_failure">🔐 Échec d'authentification</option>
                    <option value="auth_success">✅ Authentification réussie</option>
                    <option value="rate_limit_exceeded">⏱️ Limite dépassée</option>
                    <option value="unauthorized_access">🚫 Accès non autorisé</option>
                    <option value="suspicious_activity">⚠️ Activité suspecte</option>
                    <option value="invalid_token">🔑 Token invalide</option>
                    <option value="permission_denied">🚪 Permission refusée</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Niveau de sévérité</label>
                  <select
                    value={filters.severity}
                    onChange={(e) => handleFilterChange('severity', e.target.value)}
                    className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                  >
                    <option value="">Tous les niveaux</option>
                    <option value="critical">🔴 Critique</option>
                    <option value="high">🟠 Élevée</option>
                    <option value="medium">🟡 Moyenne</option>
                    <option value="low">🟢 Faible</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">ID Utilisateur</label>
                  <input
                    type="number"
                    value={filters.user_id}
                    onChange={(e) => handleFilterChange('user_id', e.target.value)}
                    className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                    placeholder="Ex: 123"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Adresse IP</label>
                  <input
                    type="text"
                    value={filters.ip_address}
                    onChange={(e) => handleFilterChange('ip_address', e.target.value)}
                    className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all font-mono text-sm"
                    placeholder="192.168.1.1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Date début</label>
                  <input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => handleFilterChange('start_date', e.target.value)}
                    className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Date fin</label>
                  <input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => handleFilterChange('end_date', e.target.value)}
                    className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                  />
                </div>
              </div>
            )}
          </Card>

          {/* Table des logs avec design moderne */}
          <Card className="bg-gray-900/60 backdrop-blur-sm border-gray-700/50 overflow-hidden">
            <div className="p-6 border-b border-gray-700/50">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white flex items-center space-x-2">
                  <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Événements de sécurité</span>
                </h2>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowSeverityLegend((prev) => !prev)}
                      className="h-8 w-8 rounded-full border border-gray-600 bg-gray-800/70 text-gray-200 hover:bg-gray-700 transition-colors"
                      aria-label="Afficher la légende des niveaux de sévérité"
                      title="Légende des niveaux"
                    >
                      ?
                    </button>
                    {showSeverityLegend && (
                      <div className="absolute right-0 top-10 z-20 w-72 rounded-lg border border-gray-600 bg-gray-900 p-3 shadow-xl">
                        <p className="text-sm font-semibold text-white mb-2">Légende des niveaux</p>
                        <ul className="space-y-1.5 text-xs text-gray-300">
                          <li><span className="text-red-400 font-semibold">Critique</span> : action immédiate, risque fort.</li>
                          <li><span className="text-orange-400 font-semibold">Élevée</span> : traitement prioritaire dans la journée.</li>
                          <li><span className="text-yellow-400 font-semibold">Moyenne</span> : à investiguer rapidement, impact modéré.</li>
                          <li><span className="text-green-400 font-semibold">Faible</span> : information/veille, impact limité.</li>
                        </ul>
                      </div>
                    )}
                  </div>
                  <span className="text-sm text-gray-400">
                    {logs.length} événement{logs.length > 1 ? 's' : ''} affiché{logs.length > 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-16">
                <LoadingSpinner />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-16">
                <div className="bg-gray-800/50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-gray-400 text-lg">Aucun log trouvé</p>
                <p className="text-gray-500 text-sm mt-2">Essayez de modifier vos filtres de recherche</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-800/50 border-b border-gray-700/50">
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Date & Heure</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Sévérité</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Utilisateur</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Adresse IP</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Endpoint</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Détails</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/50">
                      {logs.map((log, index) => (
                        <tr 
                          key={log.id} 
                          className="hover:bg-gray-800/30 transition-colors duration-150 group"
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-300 font-medium">
                              {new Date(log.created_at).toLocaleDateString('fr-FR', { 
                                day: '2-digit', 
                                month: 'short',
                                year: 'numeric'
                              })}
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(log.created_at).toLocaleTimeString('fr-FR', { 
                                hour: '2-digit', 
                                minute: '2-digit',
                                second: '2-digit'
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <span className="text-lg">{getTypeIcon(log.type)}</span>
                              <span className="text-sm text-gray-300 font-medium">
                                {getTypeLabel(log.type)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center space-x-1 px-3 py-1.5 rounded-full text-xs font-semibold ${getSeverityColor(log.severity)}`}>
                              {getSeverityIcon(log.severity)}
                              <span>{log.severity.toUpperCase()}</span>
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {log.username ? (
                              <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                  {log.username.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-sm text-gray-300 font-medium">{log.username}</span>
                              </div>
                            ) : log.user_id ? (
                              <span className="text-sm text-gray-400">ID: {log.user_id}</span>
                            ) : (
                              <span className="text-sm text-gray-500 italic">Anonyme</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-3 py-1.5 bg-gray-800/50 rounded-lg text-xs font-mono text-gray-300 border border-gray-700/50">
                              {log.ip_address}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                log.method === 'GET' ? 'bg-green-500/20 text-green-400' :
                                log.method === 'POST' ? 'bg-blue-500/20 text-blue-400' :
                                log.method === 'PUT' ? 'bg-yellow-500/20 text-yellow-400' :
                                log.method === 'DELETE' ? 'bg-red-500/20 text-red-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>
                                {log.method}
                              </span>
                              <span className="text-xs text-gray-400 font-mono truncate max-w-xs">
                                {log.path}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-400 max-w-md truncate group-hover:max-w-none transition-all" title={log.details || ''}>
                              {log.details || (
                                <span className="text-gray-600 italic">Aucun détail</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination moderne */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-700/50 bg-gray-800/30">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-400">
                        Page <span className="font-semibold text-white">{page}</span> sur <span className="font-semibold text-white">{totalPages}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                          <span>Précédent</span>
                        </button>
                        <div className="flex items-center space-x-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum: number;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (page <= 3) {
                              pageNum = i + 1;
                            } else if (page >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = page - 2 + i;
                            }
                            return (
                              <button
                                key={pageNum}
                                onClick={() => setPage(pageNum)}
                                className={`px-3 py-2 rounded-lg transition-all ${
                                  page === pageNum
                                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                                    : 'bg-gray-700/50 hover:bg-gray-700 text-gray-300'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                        <button
                          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                          className="px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center space-x-2"
                        >
                          <span>Suivant</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SecurityLogs;
